import { type FC, type FormEvent, useEffect, useState } from 'react';
import oauthApi, {
  type IssuedOAuthClient,
  type OAuthClientSummary,
} from '@/api/oauth.api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import ConfirmDialog from '@/components/ui/ConfirmDialog';

export const OAuthClientsPage: FC = () => {
  const [clients, setClients] = useState<OAuthClientSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [redirect, setRedirect] = useState('');
  const [creating, setCreating] = useState(false);
  const [justCreated, setJustCreated] = useState<IssuedOAuthClient | null>(null);
  const [revokeId, setRevokeId] = useState<string | null>(null);
  const [revoking, setRevoking] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      setClients(await oauthApi.listClients());
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !redirect.trim()) return;
    setCreating(true);
    try {
      const issued = await oauthApi.createClient(name.trim(), [redirect.trim()]);
      setJustCreated(issued);
      setName('');
      setRedirect('');
      setClients((prev) => [
        {
          id: issued.id,
          clientId: issued.clientId,
          name: issued.name,
          redirectUris: issued.redirectUris,
          origin: issued.origin,
          createdAt: issued.createdAt,
        },
        ...prev,
      ]);
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeId) return;
    setRevoking(true);
    try {
      await oauthApi.deleteClient(revokeId);
      setClients((prev) => prev.filter((c) => c.id !== revokeId));
    } finally {
      setRevoking(false);
      setRevokeId(null);
    }
  };

  const copy = (txt: string) => navigator.clipboard?.writeText(txt).catch(() => {});
  const revokeTarget = clients.find((c) => c.id === revokeId);

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <h1 style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, marginBottom: 'var(--spacing-sm)' }}>
        Clients OAuth
      </h1>
      <p
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-muted)',
          marginBottom: 'var(--spacing-lg)',
          lineHeight: 1.5,
        }}
      >
        À utiliser pour brancher Claude.ai ou ChatGPT Enterprise au MCP StratMap. Renseigne le{' '}
        <strong>redirect URI</strong> fourni par Claude/ChatGPT au moment où tu crées le connecteur,
        puis colle le <code>client_id</code> et <code>client_secret</code> affichés une seule fois ci-dessous.
      </p>

      {justCreated && (
        <div
          style={{
            backgroundColor: '#10B98111',
            border: '1px solid #10B981',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--spacing-md)',
            marginBottom: 'var(--spacing-lg)',
          }}
        >
          <div style={{ marginBottom: 8, fontWeight: 600 }}>Client créé — copie les deux valeurs maintenant :</div>

          <Field label="client_id" value={justCreated.clientId} onCopy={copy} />
          <Field label="client_secret" value={justCreated.clientSecret} onCopy={copy} />

          <div style={{ marginTop: 8, textAlign: 'right' }}>
            <Button size="sm" variant="ghost" onClick={() => setJustCreated(null)}>
              J'ai copié, masquer
            </Button>
          </div>
        </div>
      )}

      <form
        onSubmit={handleCreate}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-sm)',
          marginBottom: 'var(--spacing-xl)',
          padding: 'var(--spacing-md)',
          backgroundColor: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
        }}
      >
        <Input
          label="Nom du client"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="ex : Claude.ai personnel"
          required
        />
        <Input
          label="Redirect URI (fourni par le connecteur)"
          value={redirect}
          onChange={(e) => setRedirect(e.target.value)}
          placeholder="https://claude.ai/api/mcp/auth_callback"
          required
        />
        <Button type="submit" isLoading={creating} disabled={!name.trim() || !redirect.trim()}>
          Créer un client
        </Button>
      </form>

      <h2 style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, marginBottom: 'var(--spacing-sm)' }}>
        Clients actifs
      </h2>

      {loading && <p style={{ color: 'var(--color-text-muted)' }}>Chargement…</p>}
      {!loading && clients.length === 0 && (
        <p style={{ color: 'var(--color-text-muted)' }}>Aucun client OAuth pour l'instant.</p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
        {clients.map((c) => (
          <div
            key={c.id}
            style={{
              padding: 'var(--spacing-sm) var(--spacing-md)',
              backgroundColor: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-md)',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600 }}>
                {c.name}{' '}
                <span
                  style={{
                    fontSize: 10,
                    textTransform: 'uppercase',
                    padding: '2px 6px',
                    marginLeft: 6,
                    borderRadius: 4,
                    backgroundColor: c.origin === 'dynamic' ? '#3B82F622' : '#8B5CF622',
                    color: c.origin === 'dynamic' ? '#3B82F6' : '#8B5CF6',
                  }}
                >
                  {c.origin === 'dynamic' ? 'auto' : 'manuel'}
                </span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--color-text-muted)', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {c.clientId}
              </div>
              <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2 }}>
                Redirects: {c.redirectUris.join(', ')}
              </div>
            </div>
            <Button size="sm" variant="danger" onClick={() => setRevokeId(c.id)}>
              Supprimer
            </Button>
          </div>
        ))}
      </div>

      <ConfirmDialog
        open={revokeId !== null}
        title="Supprimer ce client OAuth ?"
        message={
          <p>
            Le client <strong>{revokeTarget?.name}</strong> ne pourra plus échanger de code.
            Les tokens déjà émis restent valides jusqu'à leur expiration (révoque-les
            depuis « Jetons API » si nécessaire).
          </p>
        }
        confirmLabel="Supprimer"
        destructive
        busy={revoking}
        onConfirm={handleRevoke}
        onCancel={() => setRevokeId(null)}
      />
    </div>
  );
};

const Field: FC<{ label: string; value: string; onCopy: (v: string) => void }> = ({
  label,
  value,
  onCopy,
}) => (
  <div style={{ marginTop: 8 }}>
    <div style={{ fontSize: 11, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
      {label}
    </div>
    <div
      style={{
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        backgroundColor: 'var(--color-background)',
        borderRadius: 'var(--radius-md)',
        padding: '8px 12px',
        fontFamily: 'monospace',
        fontSize: 12,
      }}
    >
      <span style={{ flex: 1, wordBreak: 'break-all' }}>{value}</span>
      <Button size="sm" variant="secondary" onClick={() => onCopy(value)}>
        Copier
      </Button>
    </div>
  </div>
);

export default OAuthClientsPage;
